import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryContextSettingID } from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    DatasourceColumnId,
    generateTableFullPath,
    ICompiledSQLTokens,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { BiVizDashboardFunnels } from '../sql-maps/biviz-dashboard-funnels-datasource-sql-map';

export interface CustomFunnelsUsersByStepDataLoaderFactoryOptions
    extends ICreatedInstanceId, IGcpProjectId, IQueryCacheService {}

export class CustomFunnelsUsersByStepDataLoaderFactory extends Factory<
    DataLoader,
    CustomFunnelsUsersByStepDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'CustomFunnelsUsersByStepDataLoaderFactory';

    getInstance(options: CustomFunnelsUsersByStepDataLoaderFactoryOptions) {
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );
                console.log(
                    'CustomFunnelsUsersByStepDataLoaderFactory',
                    queryContext
                );

                const ignoredContextColumnIds: DatasourceColumnId[] = [];

                const datasources = {
                    BiVizDashboardFunnels,
                } as const;
                const datasource = datasources['BiVizDashboardFunnels'];

                const datasourceSqlMap = datasource.sqlMap;

                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const dsColumnsStatus = DataLoader.getMissingColumnsStatus(
                    dsColumns.missing
                );

                dsColumns.present = dsColumns.present.filter(
                    (col) => !ignoredContextColumnIds.includes(col.id)
                );

                const openClosed =
                    queryContext.settings[
                        QueryContextSettingID.CUSTOM_FUNNELS_OPEN_CLOSED
                    ] ?? 'open';
                const closedFunnelSQL: Partial<ICompiledSQLTokens> =
                    openClosed === 'open'
                        ? {}
                        : {
                              where: [`closed IS TRUE`],
                          };

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroup,
                    splitGroupBy,
                    where,
                    join,
                    windowTotal,
                } = datasource.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options,
                    mergeSqlTokensArray([closedFunnelSQL])
                );

                const query = `
-- DataLoaderId: ${this.instanceId}
WITH by_step_name AS 
(
  SELECT
    ${splitSelect}step_id
    , step_name
    , step_group

    -- energy
    , APPROX_QUANTILES(resources.energy_spent_since_first_step, 4 RESPECT NULLS) AS used_energy_quants
    , COUNTIF(resources.energy_spent_since_first_step IS NOT NULL) AS used_energy_not_null_cnt
    , COUNTIF(resources.energy_spent_since_first_step IS NOT NULL) / COUNT(*) AS used_energy_not_null_perc
    , APPROX_QUANTILES(CAST(resources.energy_current AS INT64), 4 RESPECT NULLS) AS energy_current_quants

    -- gems
    , APPROX_QUANTILES(CAST(resources.gems_current AS INT64), 4 RESPECT NULLS) AS gems_current_quants

    -- hints
    , APPROX_QUANTILES(resources.hints_spent_since_first_step, 4 RESPECT NULLS) AS used_hints_quants
    , APPROX_QUANTILES(CAST(resources.hints_current AS INT64), 4 RESPECT NULLS) AS hints_current_quants

    -- time
    , APPROX_QUANTILES(time_since_previous_step_sec, 4 RESPECT NULLS) AS tsp_quants_sec
    , APPROX_QUANTILES(time_since_first_step_sec, 4 RESPECT NULLS) AS tsf_quants_sec
    , APPROX_QUANTILES(time_since_first_step_min, 4) AS tsf_quants_min
    , APPROX_QUANTILES(total_time_sec, 4) AS tss_quants_min

    , COUNT(DISTINCT player_id) AS users_by_step
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\` ds
  ${join}
  ${where}
  GROUP BY ALL
),

first_previous_step AS 
(
  SELECT
    *
    , FIRST_VALUE(users_by_step) OVER (${windowTotal} ORDER BY step_id) AS first_step_cnt
    , LAG(users_by_step) OVER (${windowTotal} ORDER BY step_id) AS previous_step_cnt
  FROM by_step_name
)

SELECT
  ${splitGroupBy}step_name
  , step_group
  , step_id
  , CAST(step_id AS STRING) AS step_id_str
  , users_by_step
  , 1 AS balance
  , SAFE_DIVIDE(users_by_step, first_step_cnt) AS percentage_of_first_step
  , SAFE_DIVIDE(users_by_step, previous_step_cnt) AS percentage_of_previous_step
  , 1 - SAFE_DIVIDE(users_by_step, previous_step_cnt) AS percentage_change_from_previous_step

  , FORMAT('%s-%s-%s',
      COALESCE(CAST(used_energy_quants[SAFE_OFFSET(1)] AS STRING), ' ')
      , COALESCE(CAST(used_energy_quants[SAFE_OFFSET(2)] AS STRING), ' ')
      , COALESCE(CAST(used_energy_quants[SAFE_OFFSET(3)] AS STRING), ' ')
  ) AS used_energy
  , COALESCE(CAST(used_energy_quants[OFFSET(2)] AS STRING), ' ') AS used_energy_Q2

  , CONCAT(
      ROUND(used_energy_not_null_perc * 100, 2), '%'
       , ', ('
       , used_energy_not_null_cnt, ' events)'
  ) AS used_energy_not_null
   
  , FORMAT('%s-%s-%s',
      COALESCE(CAST(energy_current_quants[OFFSET(1)] AS STRING), ' ')
      , COALESCE(CAST(energy_current_quants[OFFSET(2)] AS STRING), ' ')
      , COALESCE(CAST(energy_current_quants[OFFSET(3)] AS STRING), ' ')
  ) AS energy_current
  , COALESCE(CAST(energy_current_quants[OFFSET(2)] AS STRING), ' ') AS energy_current_Q2

  , FORMAT('%s-%s-%s',
      COALESCE(CAST(gems_current_quants[OFFSET(1)] AS STRING), ' ')
      , COALESCE(CAST(gems_current_quants[OFFSET(2)] AS STRING), ' ')
      , COALESCE(CAST(gems_current_quants[OFFSET(3)] AS STRING), ' ')
  ) AS gems_current
  , COALESCE(CAST(gems_current_quants[OFFSET(2)] AS STRING), ' ') AS gems_current_Q2 
  
  , FORMAT('%s-%s-%s',
      COALESCE(CAST(used_hints_quants[SAFE_OFFSET(1)] AS STRING), ' ')
      , COALESCE(CAST(used_hints_quants[SAFE_OFFSET(2)] AS STRING), ' ')
      , COALESCE(CAST(used_hints_quants[SAFE_OFFSET(3)] AS STRING), ' ')
  ) AS used_hints
  , COALESCE(CAST(used_hints_quants[OFFSET(2)] AS STRING), ' ') AS used_hints_Q2

  , FORMAT('%s-%s-%s',
      COALESCE(CAST(hints_current_quants[OFFSET(1)] AS STRING), ' ')
      , COALESCE(CAST(hints_current_quants[OFFSET(2)] AS STRING), ' ')
      , COALESCE(CAST(hints_current_quants[OFFSET(3)] AS STRING), ' ')
  ) AS hints_current
  , COALESCE(CAST(hints_current_quants[OFFSET(2)] AS STRING), ' ') AS hints_current_Q2 

  , FORMAT('%i-%i-%i [sec]',
      tsp_quants_sec[OFFSET(1)]
      , tsp_quants_sec[OFFSET(2)]
      , tsp_quants_sec[OFFSET(3)]
  ) AS time_since_previous_step_sec
  ,  tsp_quants_sec[OFFSET(2)] AS time_since_previous_step_sec_Q2

  , FORMAT('%.2f-%.2f-%.2f [min]',
      tsf_quants_sec[OFFSET(1)] / 60
      , tsf_quants_sec[OFFSET(2)] / 60
      , tsf_quants_sec[OFFSET(3)] / 60
  ) AS time_since_first_step_min
  , ROUND(tsf_quants_sec[OFFSET(2)] / 60, 2) AS time_since_first_step_min_Q2 
  
  /*
  , FORMAT('%i-%i-%i [min]'
      , tsf_quants_min[OFFSET(1)]
      , tsf_quants_min[OFFSET(2)]
      , tsf_quants_min[OFFSET(3)]
  ) AS time_since_first_step_min
  , ROUND(tsf_quants_min[OFFSET(2)], 2) AS time_since_first_step_min_Q2 
  */
 
  , FORMAT('%i-%i-%i [min]',
      tss_quants_min[OFFSET(1)]
      , tss_quants_min[OFFSET(2)]
      , tss_quants_min[OFFSET(3)]
  ) AS time_since_first_session
  , ROUND(tss_quants_min[OFFSET(2)], 2) AS time_since_first_session_min_Q2

  FROM first_previous_step
ORDER BY step_id${splitGroup ? `, ${splitGroup}` : ''};
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );

                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: dsColumnsStatus.pendingDataLoaderResultStatus,
                    statusMessage: dsColumnsStatus.pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
